import type { ReactNode } from 'react';

/**
 * Per-app accent colors for the suite shell.
 *
 * These drive the `--accent` CSS custom property that the shell sets on its
 * root element; nav active-states, badges and the app switcher read from it via
 * `color-mix(...)`. Sourced from the suite UI mockup.
 */
export const APP_ACCENTS = {
  hub: '#1e293b',
  tracker: '#0014cc',
  asset: '#7c3aed',
  channel: '#0f766e',
} as const;

export type SuiteAppId = keyof typeof APP_ACCENTS;

/**
 * A single sidebar navigation entry.
 *
 * Framework-agnostic: `icon` is a `ReactNode` so consumers can pass a rendered
 * lucide icon (or anything else) without shared-ui depending on an icon lib.
 *
 * Modeled as a union so every item is valid by construction: a **link** item
 * requires a `path` (renders as a router link); an **action** item requires an
 * `onClick` and has no `path` (renders as a button, e.g. a notifications
 * trigger). Items with neither are rejected at compile time.
 */
interface SuiteNavItemBase {
  title: string;
  icon?: ReactNode;
  /** Numeric badge shown on the item (e.g. unread count). */
  badge?: number;
}

export interface SuiteNavLinkItem extends SuiteNavItemBase {
  /** Router path this item links to. */
  path: string;
  /** Extra path prefixes that should also mark this item active. */
  matchPrefixes?: string[];
  /** Optional side effect on click (navigation still happens). */
  onClick?: () => void;
}

export interface SuiteNavActionItem extends SuiteNavItemBase {
  path?: never;
  /** Action items never link, so they carry no active-match prefixes. */
  matchPrefixes?: never;
  /** Action handler. Renders the item as a button. */
  onClick: () => void;
}

export type SuiteNavItem = SuiteNavLinkItem | SuiteNavActionItem;

/**
 * Config for one application shown in the suite shell / switcher.
 *
 * The shell is entirely config-driven — no consumer-specific imports live in
 * shared-ui. Apps pass their nav, identity and accent as data.
 */
export interface SuiteAppConfig {
  id: string;
  /** Display name, e.g. "Content Tracker". */
  name: string;
  /** Short descriptor shown under the name in the switcher. */
  subtitle?: string;
  /** Hex accent color. Defaults are available in {@link APP_ACCENTS}. */
  accent: string;
  /** Short glyph/initials for the app icon tile, e.g. "CT". */
  iconLabel: string;
  /** Longer description used on the hub app-card. */
  description?: string;
  /** Sidebar navigation for this app. */
  navItems: SuiteNavItem[];
}
